import { config } from 'src/config'
import { cartItemSellingPlanGenerator } from 'src/functions/product/subscriptionSellingPlanGenerator'
import { getMetafield } from 'src/functions/util/getMetafield'

import type { CartLineUpdateInput, CartLine, AttributeInput, CartLineInput } from 'src/data/graphql-generated'

type CartLineUpdateArrayProps = {
  item: CartLine
  newQuantity?: number
  newSellingPlanInterval?: number
}

type CreateMatchingNewQuantityDonationProps = {
  itemCartLine: CartLine
  currentCartLines: CartLine[]
  newQuantity?: number
  currentSellingPlanInterval?: number
}

type CreateMatchingDonationWithUpdatedSellingPlanProps = {
  itemCartLine: CartLine
  currentCartLines: CartLine[]
  currentSellingPlanInterval?: number
  newSellingPlanInterval?: number
}

type CreateMatchingDonationWithUpdatedSellingPlanResult = {
  toAdd?: CartLineInput
  toUpdate?: CartLineUpdateInput
}

export const initialiseCartLinesUpdateArray = ({
  item,
  newQuantity,
  newSellingPlanInterval,
}: CartLineUpdateArrayProps): CartLineUpdateInput[] => {
  if (!newSellingPlanInterval) {
    return [{ id: item.id, quantity: newQuantity }]
  }
  const {
    id,
    merchandise: { id: merchandiseId, sellingPlanAllocations },
    sellingPlanAllocation,
  } = item
  const { sellingPlans } = cartItemSellingPlanGenerator(sellingPlanAllocation ? sellingPlanAllocations.nodes : [])
  const { coffeeSellingPlanId } = sellingPlans[newSellingPlanInterval || 0]

  return [{ id, merchandiseId, sellingPlanId: coffeeSellingPlanId }]
}

export const createMatchingNewQuantityDonation = ({
  itemCartLine,
  currentCartLines,
  currentSellingPlanInterval,
  newQuantity,
}: CreateMatchingNewQuantityDonationProps): CartLineUpdateInput | undefined => {
  if (newQuantity !== 0 && !newQuantity) return undefined

  const {
    quantity,
    merchandise: { metafields, sku },
    sellingPlanAllocation,
  } = itemCartLine

  const donationVariantId = getMetafield(metafields, 'donation', 'variant')

  if (!donationVariantId) return undefined

  const { donationAttributeKey } = config
  const attributeValueToFindDonationMatch = sellingPlanAllocation ? `${currentSellingPlanInterval}W` : sku

  const donationCartLine = currentCartLines.find(
    (cartLine) =>
      cartLine.merchandise.id === donationVariantId &&
      cartLine.attributes.find(
        (attribute) => attribute.key === donationAttributeKey && attribute.value === attributeValueToFindDonationMatch,
      ),
  )

  if (donationCartLine) {
    const itemQuantityDiff = newQuantity - quantity
    const newDonationQuantity = donationCartLine.quantity + itemQuantityDiff

    const donationCartLineUpdateObject = {
      id: donationCartLine.id,
      quantity: newDonationQuantity >= 0 ? newDonationQuantity : 0,
      attributes: [
        ...(donationCartLine.attributes.filter((a) => a.key !== donationAttributeKey) as AttributeInput[]),
        { key: donationAttributeKey, value: attributeValueToFindDonationMatch as string },
      ],
    }

    return donationCartLineUpdateObject
  }

  return undefined
}

export const createMatchingDonationWithUpdatedSellingPlan = ({
  itemCartLine,
  currentCartLines,
  currentSellingPlanInterval,
  newSellingPlanInterval,
}: CreateMatchingDonationWithUpdatedSellingPlanProps): CreateMatchingDonationWithUpdatedSellingPlanResult => {
  if (!currentSellingPlanInterval || !newSellingPlanInterval) {
    return {}
  }

  const {
    quantity,
    merchandise: { metafields },
  } = itemCartLine

  const donationVariantId = getMetafield(metafields, 'donation', 'variant')

  if (!donationVariantId) {
    return {}
  }

  // * find the donation cart line
  const attributeValueToFindDonationMatch = `${currentSellingPlanInterval}W`
  const { donationAttributeKey } = config

  const donationCartLine = currentCartLines.find(
    (cartLine) =>
      cartLine.merchandise.id === donationVariantId &&
      cartLine.attributes.find(
        (attribute) => attribute.key === donationAttributeKey && attribute.value === attributeValueToFindDonationMatch,
      ),
  )

  if (donationCartLine) {
    const attributeValueToUpdateDonationMatch = `${newSellingPlanInterval}W`

    if (itemCartLine.quantity < donationCartLine.quantity) {
      const newDonationQuantity = donationCartLine.quantity - itemCartLine.quantity

      return {
        toAdd: {
          merchandiseId: donationCartLine.merchandise.id,
          attributes: [{ key: donationAttributeKey, value: attributeValueToUpdateDonationMatch }],
          quantity: itemCartLine.quantity,
        },
        toUpdate: {
          id: donationCartLine.id,
          quantity: newDonationQuantity >= 0 ? newDonationQuantity : 0,
        },
      }
    }

    return {
      toUpdate: {
        id: donationCartLine.id,
        attributes: [
          ...(donationCartLine.attributes.filter((a) => a.key !== donationAttributeKey) as AttributeInput[]),
          { key: donationAttributeKey, value: attributeValueToUpdateDonationMatch },
        ],
        quantity,
      },
    }
  }

  return {}
}
